import _ from 'lodash';
import { GraphError, GraphResult } from './types';

export type QuickFixList = {
  readonly name: string;
  readonly err: GraphError;
  readonly children: readonly {
    readonly name: string;
    readonly via: readonly string[];
  }[];
}[];

export const quickFix = (
  results: Record<string, GraphResult>
): QuickFixList => {
  const topLevelList: Record<string, GraphError> = {};
  const depErrors: Record<string, string> = {};

  for (const [name, res] of Object.entries(results)) {
    if (res.isErr()) {
      if (res.error.err.t === 'dep-has-error') {
        depErrors[name] = res.error.err.dep;
      } else {
        topLevelList[name] = res.error.err;
      }
    }
  }

  const errMap = groupByRootSym(depErrors);

  const quickFix: Record<
    string,
    { err: GraphError; children: { name: string; via: string[] }[] }
  > = _.mapValues(topLevelList, (err, name) => ({
    err,
    children: _.sortBy(errMap[name] ?? [], ({ name, via }) => [
      via.length,
      name,
    ]),
  }));

  const list = Object.entries(quickFix).map(([name, item]) => ({
    ...item,
    name,
  }));

  return _.sortBy(list, (item) => item.name);
};

function groupByRootSym(
  input: Record<string, string>
): Record<string, { name: string; via: string[] }[]> {
  const grouped: Record<string, { name: string; via: string[] }[]> = {};

  for (const start of Object.keys(input)) {
    const seen = new Set<string>();
    const path: string[] = [];
    let current: string = start;

    while (input[current] !== undefined && !seen.has(current)) {
      if (!seen.has(current)) {
        seen.add(current);
        path.push(current);
        current = input[current]!;
      }
    }

    const root = current;
    const fullPath = [...path, root];
    const via = fullPath.slice(1); // exclude the 'from' symbol

    if (!grouped[root]) grouped[root] = [];

    grouped[root].push({
      name: start,
      via,
    });
  }

  return grouped;
}
